import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Settings, Lock, Users, Building2, Search, Shield, AlertCircle } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import AccessManagementModal from '@/components/AccessManagementModal'

/**
 * Types for presentation access management
 */
interface PresentationData {
  id: string
  title: string
  description: string | null
  category: string | null
  language: string | null
  thumbnailUrl: string | null
  duration: string | null
  slideCount: number | null
  tags: string[] | null
  isActive: boolean
  accessible: boolean
  createdAt: string
  updatedAt: string
}

interface AccessSummary {
  roles: number
  users: number
  companies: number
  total: number
}

interface PresentationWithAccess extends PresentationData {
  accessSummary?: AccessSummary
}

export default function PresentationAccessManagement() {
  const navigate = useNavigate()

  // Get user from localStorage
  const userStr = localStorage.getItem('user')
  const currentUser = userStr ? JSON.parse(userStr) : null

  // Local state
  const [presentations, setPresentations] = useState<PresentationWithAccess[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  // Bulk operation state
  const [bulkModalOpen, setBulkModalOpen] = useState(false)
  const [bulkAction, setBulkAction] = useState<'grant' | 'revoke'>('grant')
  const [selectedRole, setSelectedRole] = useState<string>('')
  const [bulkInProgress, setBulkInProgress] = useState(false)
  const [bulkProgress, setBulkProgress] = useState({ completed: 0, total: 0 })
  const [confirmationOpen, setConfirmationOpen] = useState(false)

  // Modal state
  const [selectedPresentation, setSelectedPresentation] = useState<PresentationData | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Filter presentations based on search query
  const filteredPresentations = useMemo(() => {
    if (!searchQuery.trim()) {
      return presentations
    }

    const query = searchQuery.toLowerCase()
    return presentations.filter(pres =>
      pres.title.toLowerCase().includes(query) ||
      (pres.description && pres.description.toLowerCase().includes(query)) ||
      (pres.category && pres.category.toLowerCase().includes(query))
    )
  }, [presentations, searchQuery])

  // Fetch presentations and access summaries
  useEffect(() => {
    if (!currentUser) {
      navigate('/login')
      return
    }

    // Check if user is super admin
    if (currentUser.role !== 'super_admin') {
      navigate('/dashboard')
      return
    }

    fetchPresentations()
  }, [navigate, currentUser])

  const fetchPresentations = async () => {
    try {
      setLoading(true)
      setError(null)

      // Fetch all presentations from presentation-library API
      const response = await fetch('http://localhost:3007/api/presentation-library', {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch presentations: ${response.statusText}`)
      }

      const data = await response.json()

      if (data.success && data.data) {
        // Fetch access summary for each presentation
        const presentationsWithAccess = await Promise.all(
          data.data.map(async (pres: PresentationData) => {
            try {
              const accessResponse = await fetch(
                `http://localhost:3007/api/presentation-library/${pres.id}/access`,
                {
                  credentials: 'include',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                }
              )

              if (accessResponse.ok) {
                const accessData = await accessResponse.json()
                if (accessData.success && accessData.data) {
                  // Calculate access summary
                  const roles = accessData.data.filter((a: any) => a.role !== null).length
                  const users = accessData.data.filter((a: any) => a.userId !== null).length
                  const companies = accessData.data.filter((a: any) => a.companyId !== null).length

                  return {
                    ...pres,
                    accessSummary: {
                      roles,
                      users,
                      companies,
                      total: roles + users + companies,
                    },
                  }
                }
              }
            } catch (err) {
              console.error(`Failed to fetch access for ${pres.id}:`, err)
            }

            // Return presentation without access summary if fetch fails
            return {
              ...pres,
              accessSummary: {
                roles: 0,
                users: 0,
                companies: 0,
                total: 0,
              },
            }
          })
        )

        setPresentations(presentationsWithAccess)
      } else {
        throw new Error(data.error || 'Failed to fetch presentations')
      }
    } catch (err) {
      console.error('Error fetching presentations:', err)
      setError(err instanceof Error ? err.message : 'Failed to load presentations')
    } finally {
      setLoading(false)
    }
  }

  const handleManageAccess = (presentation: PresentationData) => {
    setSelectedPresentation(presentation)
    setIsModalOpen(true)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setSelectedPresentation(null)
  }

  const handleAccessChangeSuccess = () => {
    // Refresh presentations to update access summaries
    fetchPresentations()
  }

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(filteredPresentations.map(p => p.id))
      setSelectedIds(allIds)
    } else {
      setSelectedIds(new Set())
    }
  }

  const handleSelectOne = (id: string, checked: boolean) => {
    const newSelectedIds = new Set(selectedIds)
    if (checked) {
      newSelectedIds.add(id)
    } else {
      newSelectedIds.delete(id)
    }
    setSelectedIds(newSelectedIds)
  }

  const isAllSelected = filteredPresentations.length > 0 && selectedIds.size === filteredPresentations.length

  // Bulk operation handlers
  const openBulkModal = (action: 'grant' | 'revoke') => {
    setBulkAction(action)
    setSelectedRole('')
    setBulkModalOpen(true)
  }

  const handleBulkAction = async () => {
    if (!selectedRole || selectedIds.size === 0) return

    setBulkModalOpen(false)
    setConfirmationOpen(true)
  }

  const confirmBulkAction = async () => {
    setConfirmationOpen(false)
    setBulkInProgress(true)
    setBulkProgress({ completed: 0, total: selectedIds.size })

    const ids = Array.from(selectedIds)

    for (let i = 0; i < ids.length; i++) {
      const presentationId = ids[i]

      try {
        if (bulkAction === 'grant') {
          // Grant access to role
          await fetch(`http://localhost:3007/api/presentation-library/${presentationId}/access`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              role: selectedRole,
              notes: `Bulk granted via access management`,
            }),
          })
        } else {
          // Revoke access from role
          // First, get all access rules for this presentation
          const accessResponse = await fetch(
            `http://localhost:3007/api/presentation-library/${presentationId}/access`,
            {
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
            }
          )

          if (accessResponse.ok) {
            const accessData = await accessResponse.json()
            if (accessData.success && accessData.data) {
              // Find the access rule for the selected role
              const roleAccessRule = accessData.data.find(
                (rule: any) => rule.role === selectedRole
              )

              if (roleAccessRule) {
                // Delete this access rule
                await fetch(
                  `http://localhost:3007/api/presentation-library/${presentationId}/access/${roleAccessRule.id}`,
                  {
                    method: 'DELETE',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                  }
                )
              }
            }
          }
        }

        setBulkProgress(prev => ({ ...prev, completed: i + 1 }))
      } catch (err) {
        console.error(`Failed to ${bulkAction} access for ${presentationId}:`, err)
      }
    }

    setBulkInProgress(false)
    setSelectedIds(new Set())

    // Refresh presentations
    await fetchPresentations()
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
          <p className="mt-4 text-muted-foreground">Loading presentations...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-orange-500" />
            <h1 className="text-3xl font-bold text-foreground">
              Presentation Access Management
            </h1>
          </div>
          <p className="text-muted-foreground mt-2">
            Control which users, roles, and companies can access presentations
          </p>
        </div>

        {/* Search and Filter */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search presentations by title, description, or category..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="text-sm text-muted-foreground">
                {filteredPresentations.length} of {presentations.length} presentations
              </div>
              {selectedIds.size > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {selectedIds.size} selected
                  </span>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => openBulkModal('grant')}
                    disabled={bulkInProgress}
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Grant Role Access
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => openBulkModal('revoke')}
                    disabled={bulkInProgress}
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Revoke Role Access
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-destructive/10 border border-destructive/50 rounded-lg p-4">
            <div className="flex">
              <svg className="w-5 h-5 text-destructive" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="ml-3">
                <p className="text-sm text-destructive">{error}</p>
              </div>
              <button
                onClick={fetchPresentations}
                className="ml-auto text-sm text-destructive hover:underline"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Presentations Table */}
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={handleSelectAll}
                    aria-label="Select all presentations"
                  />
                </TableHead>
                <TableHead className="w-[200px]">ID</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-[120px]">Category</TableHead>
                <TableHead className="w-[100px]">Language</TableHead>
                <TableHead className="w-[150px]">Access Summary</TableHead>
                <TableHead className="w-[150px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPresentations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12">
                    <div className="flex flex-col items-center gap-3">
                      <Settings className="w-12 h-12 text-muted-foreground" />
                      <p className="text-muted-foreground">
                        {searchQuery
                          ? 'No presentations match your search criteria'
                          : 'No presentations available'}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPresentations.map((presentation) => (
                  <TableRow key={presentation.id}>
                    {/* Checkbox */}
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.has(presentation.id)}
                        onCheckedChange={(checked: boolean) => handleSelectOne(presentation.id, checked)}
                        aria-label={`Select ${presentation.title}`}
                      />
                    </TableCell>

                    {/* ID */}
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {presentation.id}
                    </TableCell>

                    {/* Title */}
                    <TableCell className="font-medium">{presentation.title}</TableCell>

                    {/* Description */}
                    <TableCell className="text-muted-foreground max-w-md truncate">
                      {presentation.description || '-'}
                    </TableCell>

                    {/* Category */}
                    <TableCell>
                      {presentation.category ? (
                        <Badge variant="secondary" className="capitalize">
                          {presentation.category}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>

                    {/* Language */}
                    <TableCell>
                      {presentation.language ? (
                        <Badge variant="outline" className="uppercase">
                          {presentation.language}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>

                    {/* Access Summary */}
                    <TableCell>
                      {presentation.accessSummary && presentation.accessSummary.total > 0 ? (
                        <div className="flex items-center gap-3">
                          {presentation.accessSummary.roles > 0 && (
                            <div className="flex items-center gap-1" title={`${presentation.accessSummary.roles} role(s)`}>
                              <Users className="w-4 h-4 text-blue-500" />
                              <span className="text-sm font-medium">{presentation.accessSummary.roles}</span>
                            </div>
                          )}
                          {presentation.accessSummary.users > 0 && (
                            <div className="flex items-center gap-1" title={`${presentation.accessSummary.users} user(s)`}>
                              <Shield className="w-4 h-4 text-green-500" />
                              <span className="text-sm font-medium">{presentation.accessSummary.users}</span>
                            </div>
                          )}
                          {presentation.accessSummary.companies > 0 && (
                            <div className="flex items-center gap-1" title={`${presentation.accessSummary.companies} compan(ies)`}>
                              <Building2 className="w-4 h-4 text-purple-500" />
                              <span className="text-sm font-medium">{presentation.accessSummary.companies}</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Lock className="w-4 h-4" />
                          <span className="text-sm">No access</span>
                        </div>
                      )}
                    </TableCell>

                    {/* Actions */}
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleManageAccess(presentation)}
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Manage Access
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>

        {/* Back to Dashboard Button */}
        <div className="mt-6">
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
        </div>
      </div>

      {/* Access Management Modal */}
      {selectedPresentation && (
        <AccessManagementModal
          open={isModalOpen}
          onOpenChange={handleModalClose}
          presentationId={selectedPresentation.id}
          presentationTitle={selectedPresentation.title}
          onSuccess={handleAccessChangeSuccess}
        />
      )}

      {/* Bulk Operation Modal */}
      <Dialog open={bulkModalOpen} onOpenChange={setBulkModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {bulkAction === 'grant' ? 'Grant Role Access' : 'Revoke Role Access'}
            </DialogTitle>
            <DialogDescription>
              {bulkAction === 'grant'
                ? `Grant access to ${selectedIds.size} selected presentation(s) for a specific role`
                : `Revoke access from ${selectedIds.size} selected presentation(s) for a specific role`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Role</label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="demo">Demo User</SelectItem>
                  <SelectItem value="company_user">Company User</SelectItem>
                  <SelectItem value="company_admin">Company Admin</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium">{selectedIds.size}</span> presentation(s) will be{' '}
                {bulkAction === 'grant' ? 'granted access to' : 'revoked access from'}{' '}
                <span className="font-medium">
                  {selectedRole ? selectedRole.replace('_', ' ') : 'the selected role'}
                </span>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleBulkAction}
              disabled={!selectedRole || bulkInProgress}
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmationOpen} onOpenChange={setConfirmationOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-500" />
              Confirm Bulk Action
            </DialogTitle>
            <DialogDescription>
              This action will {bulkAction === 'grant' ? 'grant access to' : 'revoke access from'}{' '}
              <span className="font-medium">{selectedIds.size} presentation(s)</span> for the{' '}
              <span className="font-medium">{selectedRole?.replace('_', ' ')}</span> role.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              {bulkAction === 'grant'
                ? 'All users with this role will be able to view these presentations.'
                : 'Users with this role will no longer be able to view these presentations.'}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmationOpen(false)}
              disabled={bulkInProgress}
            >
              Cancel
            </Button>
            <Button
              variant={bulkAction === 'revoke' ? 'destructive' : 'default'}
              onClick={confirmBulkAction}
              disabled={bulkInProgress}
            >
              {bulkInProgress ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Progress Indicator */}
      {bulkInProgress && (
        <div className="fixed bottom-4 right-4 bg-background border border-border rounded-lg shadow-lg p-4 min-w-[300px]">
          <div className="flex items-center gap-3">
            <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-orange-500"></div>
            <div className="flex-1">
              <p className="text-sm font-medium">
                {bulkAction === 'grant' ? 'Granting' : 'Revoking'} Access
              </p>
              <p className="text-xs text-muted-foreground">
                {bulkProgress.completed} of {bulkProgress.total} completed
              </p>
              <div className="mt-2 w-full bg-secondary rounded-full h-2">
                <div
                  className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${(bulkProgress.completed / bulkProgress.total) * 100}%`
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
